// Super Admin — Social Integrations (Facebook + WhatsApp)
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Facebook, MessageCircle, Loader2, Copy, Check, Zap, TestTube2, ScrollText,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

function StatusPill({ status }) {
  const cls = status === "connected"
    ? "bg-emerald-100 text-emerald-700"
    : status === "inactive"
      ? "bg-slate-100 text-slate-500"
      : "bg-rose-100 text-rose-700";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${cls}`}>
      {status || "disconnected"}
    </span>
  );
}

function CopyBtn({ value, testId }) {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" data-testid={testId}
      onClick={async () => {
        try { await navigator.clipboard.writeText(value || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); }
        catch { /* ignore */ }
      }}
      className="inline-flex h-8 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-600 hover:bg-slate-50">
      {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function FieldIn({ label, v, on, type = "text", placeholder = "", tid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase tracking-widest text-slate-500">{label}</Label>
      <Input type={type} value={v || ""} onChange={(e) => on(e.target.value)}
        placeholder={placeholder} data-testid={tid} />
    </div>
  );
}

export default function AdminSocialIntegrationsPage() {
  const [fb, setFb] = useState(null);
  const [wa, setWa] = useState(null);
  const [pk, setPk] = useState(null);   // Picker (Google Drive + OneDrive) creds
  const [logs, setLogs] = useState([]);
  const [savingFb, setSavingFb] = useState(false);
  const [savingWa, setSavingWa] = useState(false);
  const [savingPk, setSavingPk] = useState(false);
  const [testingFb, setTestingFb] = useState(false);
  const [testingWa, setTestingWa] = useState(false);

  const load = useCallback(async () => {
    try {
      const [f, w, p, l] = await Promise.all([
        api.get("/admin/integrations/facebook/settings"),
        api.get("/admin/integrations/whatsapp/settings"),
        api.get("/admin/integrations/pickers/settings"),
        api.get("/admin/integrations/webhook-logs", { params: { limit: 20 } }),
      ]);
      setFb({ ...f.data.data, app_secret: "", access_token: "" });
      setWa({ ...w.data.data, access_token: "" });
      setPk(p.data.data);
      setLogs(l.data.data.logs || []);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const savePk = async () => {
    setSavingPk(true);
    try {
      const { data } = await api.put("/admin/integrations/pickers/settings", {
        google_client_id: pk.google_client_id || "",
        google_api_key: pk.google_api_key || "",
        google_app_id: pk.google_app_id || "",
        onedrive_client_id: pk.onedrive_client_id || "",
        onedrive_redirect_uri: pk.onedrive_redirect_uri || "",
      });
      toast.success(data.message);
      setPk(data.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingPk(false); }
  };

  const saveFb = async () => {
    setSavingFb(true);
    try {
      const body = {
        integration_name: fb.integration_name || "Facebook Meta Lead Ads",
        app_id: fb.app_id || "", page_id: fb.page_id || "",
        verify_token: fb.verify_token || "",
        default_inquiry_type: fb.default_inquiry_type || "course_inquiry",
        default_assigned_user: fb.default_assigned_user || null,
        auto_create_inquiry: !!fb.auto_create_inquiry,
        status: fb.status || "disconnected",
      };
      if (fb.app_secret) body.app_secret = fb.app_secret;
      if (fb.access_token) body.access_token = fb.access_token;
      const { data } = await api.put("/admin/integrations/facebook/settings", body);
      toast.success(data.message);
      setFb({ ...data.data, app_secret: "", access_token: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingFb(false); }
  };

  const saveWa = async () => {
    setSavingWa(true);
    try {
      const body = {
        integration_name: wa.integration_name || "WhatsApp Business Cloud",
        business_account_id: wa.business_account_id || "",
        phone_number_id: wa.phone_number_id || "",
        business_phone_number: wa.business_phone_number || "",
        verify_token: wa.verify_token || "",
        default_inquiry_type: wa.default_inquiry_type || "general_inquiry",
        default_assigned_user: wa.default_assigned_user || null,
        auto_create_inquiry: !!wa.auto_create_inquiry,
        auto_reply_enabled: !!wa.auto_reply_enabled,
        auto_reply_message: wa.auto_reply_message || "",
        status: wa.status || "disconnected",
      };
      if (wa.access_token) body.access_token = wa.access_token;
      const { data } = await api.put("/admin/integrations/whatsapp/settings", body);
      toast.success(data.message);
      setWa({ ...data.data, access_token: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingWa(false); }
  };

  const testFb = async () => {
    setTestingFb(true);
    try { const { data } = await api.post("/admin/integrations/facebook/test"); toast[data.success ? "success" : "error"](data.message); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setTestingFb(false); }
  };
  const testWa = async () => {
    setTestingWa(true);
    try { const { data } = await api.post("/admin/integrations/whatsapp/test"); toast[data.success ? "success" : "error"](data.message); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setTestingWa(false); }
  };

  if (!fb || !wa) return <DashboardLayout title="Social Integrations">Loading…</DashboardLayout>;

  return (
    <DashboardLayout title="Social Integrations"
      breadcrumbs={["Super Admin", "Integrations", "Social"]}>
      <div className="mb-4 text-sm text-slate-600">
        Auto-capture inquiries from Facebook Meta Lead Ads and WhatsApp Business Cloud into your CRM.
      </div>

      {(() => { const base = process.env.REACT_APP_BACKEND_URL || ""; if (fb) fb._fullWebhook = `${base}/api/webhooks/facebook/leads`; if (wa) wa._fullWebhook = `${base}/api/webhooks/whatsapp/messages`; return null; })()}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Facebook */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="fb-panel">
          <div className="mb-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Facebook className="h-5 w-5 text-[#1877F2]" />
              <div className="text-base font-bold text-slate-900">Facebook Meta Lead Ads</div>
            </div>
            <StatusPill status={fb.status} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FieldIn label="Integration Name" v={fb.integration_name} on={(v) => setFb({ ...fb, integration_name: v })} />
            <FieldIn label="App ID" v={fb.app_id} on={(v) => setFb({ ...fb, app_id: v })} tid="fb-app-id" />
            <FieldIn label={`App Secret ${fb.app_secret_set ? "(set — leave blank to keep)" : ""}`}
              type="password" v={fb.app_secret} on={(v) => setFb({ ...fb, app_secret: v })} tid="fb-app-secret" />
            <FieldIn label="Page ID" v={fb.page_id} on={(v) => setFb({ ...fb, page_id: v })} tid="fb-page-id" />
            <FieldIn label={`Access Token ${fb.access_token_set ? "(set — leave blank to keep)" : ""}`}
              type="password" v={fb.access_token} on={(v) => setFb({ ...fb, access_token: v })} tid="fb-access-token" />
            <FieldIn label="Verify Token" v={fb.verify_token} on={(v) => setFb({ ...fb, verify_token: v })}
              placeholder="Random string you set in Meta app" tid="fb-verify-token" />
            <div className="col-span-2 rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">Webhook URL</div>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1 text-[11px]">{fb._fullWebhook || fb.webhook_url}</code>
                <CopyBtn value={fb._fullWebhook || fb.webhook_url} testId="fb-copy-webhook" />
              </div>
              <div className="mt-2 text-[10px] text-slate-500">
                Paste this URL + your Verify Token into Meta App → Webhooks → Page → subscribe to <b>leadgen</b>.
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Default Inquiry Type</Label>
              <Select value={fb.default_inquiry_type || "course_inquiry"}
                onValueChange={(v) => setFb({ ...fb, default_inquiry_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["course_inquiry", "partnership_inquiry", "demo_request", "general_inquiry", "training_program_inquiry"].map((s) => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Status</Label>
              <Select value={fb.status || "disconnected"} onValueChange={(v) => setFb({ ...fb, status: v })}>
                <SelectTrigger data-testid="fb-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="connected">Connected</SelectItem>
                  <SelectItem value="disconnected">Disconnected</SelectItem>
                  <SelectItem value="inactive">Inactive (pause)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="col-span-2 inline-flex items-center gap-2 text-xs">
              <Switch checked={!!fb.auto_create_inquiry} onCheckedChange={(v) => setFb({ ...fb, auto_create_inquiry: v })} />
              Auto-create CRM inquiry from Facebook leads
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={testFb} disabled={testingFb}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-70"
              data-testid="fb-test-btn">
              {testingFb ? <Loader2 className="h-3 w-3 animate-spin" /> : <TestTube2 className="h-3 w-3" />}
              Test Connection
            </button>
            <button onClick={saveFb} disabled={savingFb}
              className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-xs font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70"
              data-testid="fb-save-btn">
              {savingFb ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
              Save Facebook Settings
            </button>
          </div>
        </section>

        {/* WhatsApp */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="wa-panel">
          <div className="mb-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-[#25D366]" />
              <div className="text-base font-bold text-slate-900">WhatsApp Business Cloud</div>
            </div>
            <StatusPill status={wa.status} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FieldIn label="Integration Name" v={wa.integration_name} on={(v) => setWa({ ...wa, integration_name: v })} />
            <FieldIn label="WABA ID" v={wa.business_account_id} on={(v) => setWa({ ...wa, business_account_id: v })} tid="wa-waba-id" />
            <FieldIn label="Phone Number ID" v={wa.phone_number_id} on={(v) => setWa({ ...wa, phone_number_id: v })} tid="wa-phone-id" />
            <FieldIn label="Business Phone Number" v={wa.business_phone_number} on={(v) => setWa({ ...wa, business_phone_number: v })} placeholder="+911234567890" tid="wa-business-phone" />
            <FieldIn label={`Access Token ${wa.access_token_set ? "(set — leave blank to keep)" : ""}`}
              type="password" v={wa.access_token} on={(v) => setWa({ ...wa, access_token: v })} tid="wa-access-token" />
            <FieldIn label="Verify Token" v={wa.verify_token} on={(v) => setWa({ ...wa, verify_token: v })}
              placeholder="Random string you set in Meta app" tid="wa-verify-token" />
            <div className="col-span-2 rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">Webhook URL</div>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1 text-[11px]">{wa._fullWebhook || wa.webhook_url}</code>
                <CopyBtn value={wa._fullWebhook || wa.webhook_url} testId="wa-copy-webhook" />
              </div>
              <div className="mt-2 text-[10px] text-slate-500">
                Paste this URL + Verify Token into Meta App → WhatsApp → Configuration → Webhooks, subscribe to <b>messages</b>.
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Default Inquiry Type</Label>
              <Select value={wa.default_inquiry_type || "general_inquiry"}
                onValueChange={(v) => setWa({ ...wa, default_inquiry_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["general_inquiry", "course_inquiry", "training_program_inquiry", "callback_request"].map((s) => (
                    <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-widest text-slate-500">Status</Label>
              <Select value={wa.status || "disconnected"} onValueChange={(v) => setWa({ ...wa, status: v })}>
                <SelectTrigger data-testid="wa-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="connected">Connected</SelectItem>
                  <SelectItem value="disconnected">Disconnected</SelectItem>
                  <SelectItem value="inactive">Inactive (pause)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="col-span-2 inline-flex items-center gap-2 text-xs">
              <Switch checked={!!wa.auto_create_inquiry} onCheckedChange={(v) => setWa({ ...wa, auto_create_inquiry: v })} />
              Auto-create CRM inquiry from incoming WhatsApp messages
            </label>
            <label className="col-span-2 inline-flex items-center gap-2 text-xs">
              <Switch checked={!!wa.auto_reply_enabled} onCheckedChange={(v) => setWa({ ...wa, auto_reply_enabled: v })} />
              Send auto-reply to sender
            </label>
            {wa.auto_reply_enabled && (
              <div className="col-span-2 space-y-1">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Auto-reply message</Label>
                <Textarea rows={2} value={wa.auto_reply_message || ""}
                  onChange={(e) => setWa({ ...wa, auto_reply_message: e.target.value })} />
              </div>
            )}
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={testWa} disabled={testingWa}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-70"
              data-testid="wa-test-btn">
              {testingWa ? <Loader2 className="h-3 w-3 animate-spin" /> : <TestTube2 className="h-3 w-3" />}
              Test Webhook
            </button>
            <button onClick={saveWa} disabled={savingWa}
              className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-xs font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70"
              data-testid="wa-save-btn">
              {savingWa ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
              Save WhatsApp Settings
            </button>
          </div>
        </section>
      </div>

      {/* Google Drive + OneDrive Picker credentials (used by Add Video dialog) */}
      {pk && (
        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="pickers-panel">
          <div className="mb-4 flex items-center justify-between gap-2">
            <div className="text-base font-bold text-slate-900">Cloud Picker Credentials</div>
            <div className="flex gap-1">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${pk.google_configured ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>Google Drive {pk.google_configured ? "Ready" : "Not Configured"}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${pk.onedrive_configured ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>OneDrive {pk.onedrive_configured ? "Ready" : "Not Configured"}</span>
            </div>
          </div>
          <p className="mb-3 text-xs text-slate-500">
            Add credentials so the &quot;Add Video Lesson&quot; dialog can open a native Google Drive / OneDrive file picker for admins.
            Users&apos; own files are never sent through Steward servers — the browser picks the file, and Steward downloads it via OAuth to compress + store.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <FieldIn label="Google OAuth Client ID" v={pk.google_client_id} on={(v) => setPk({ ...pk, google_client_id: v })}
              placeholder="1234567890-xxxx.apps.googleusercontent.com" tid="pk-google-client-id" />
            <FieldIn label="Google API Key (Picker API)" v={pk.google_api_key} on={(v) => setPk({ ...pk, google_api_key: v })}
              placeholder="AIzaSy…" tid="pk-google-api-key" />
            <FieldIn label="Google App ID / Project Number" v={pk.google_app_id} on={(v) => setPk({ ...pk, google_app_id: v })}
              placeholder="123456789012" tid="pk-google-app-id" />
            <FieldIn label="Microsoft App (OneDrive) Client ID" v={pk.onedrive_client_id} on={(v) => setPk({ ...pk, onedrive_client_id: v })}
              placeholder="12345678-1234-1234-1234-123456789012" tid="pk-onedrive-client-id" />
            <FieldIn label="OneDrive Redirect URI" v={pk.onedrive_redirect_uri} on={(v) => setPk({ ...pk, onedrive_redirect_uri: v })}
              placeholder={`${process.env.REACT_APP_BACKEND_URL || ""}/app/super-admin`} tid="pk-onedrive-redirect" />
          </div>
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <b>How to obtain:</b>
            <ul className="mt-1 list-disc pl-5 space-y-1">
              <li>Google: <b>console.cloud.google.com</b> → new project → enable <b>Google Drive API</b> + <b>Google Picker API</b> → OAuth consent screen → Credentials → create OAuth Client ID (Web) + API key. Add your preview domain to Authorized JavaScript origins.</li>
              <li>Microsoft: <b>portal.azure.com</b> → Azure AD → App registrations → New → Redirect URI = the field above → copy Application (client) ID.</li>
            </ul>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={savePk} disabled={savingPk}
              className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-xs font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70"
              data-testid="pk-save-btn">
              {savingPk ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
              Save Picker Credentials
            </button>
          </div>
        </section>
      )}

      {/* Webhook logs */}
      <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="webhook-logs">
        <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900">
          <ScrollText className="h-4 w-4" /> Recent Webhook Activity
        </div>
        {logs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-xs text-slate-400">
            No webhook events yet. Configure the integration above and Meta will start sending events here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100 text-left text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="py-2 pr-3">Platform</th><th className="py-2 pr-3">Event</th>
                  <th className="py-2 pr-3">Status</th><th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Error</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-slate-50 last:border-0">
                    <td className="py-2 pr-3 font-mono">{l.platform}</td>
                    <td className="py-2 pr-3">{l.event_type}</td>
                    <td className="py-2 pr-3">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                        l.status === "success" ? "bg-emerald-100 text-emerald-700" :
                        l.status === "duplicate" ? "bg-amber-100 text-amber-700" :
                        l.status === "failed" ? "bg-rose-100 text-rose-700" :
                        "bg-slate-100 text-slate-500"
                      }`}>{l.status}</span>
                    </td>
                    <td className="py-2 pr-3 text-slate-500">{new Date(l.created_at).toLocaleString()}</td>
                    <td className="py-2 pr-3 text-slate-500 truncate max-w-[200px]">{l.error_message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </DashboardLayout>
  );
}
